if (!customElements.get('product-add-ons')) {
  customElements.define(
    'product-add-ons',
    class ProductAddOns extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId || '';
        this.formId = this.dataset.formId || '';
        this.isEditMode = this.dataset.editMode === 'true';
        this.addButton = this.querySelector('.add-ons__add-cart');
        this.buttonLabel = this.querySelector('.m-add-to-cart--text');
        this.cartDrawer = document.querySelector('m-cart-drawer');
        this.currentVariant = null;
        this.basePriceCents = parseInt(this.dataset.basePrice || '', 10);
        if (Number.isNaN(this.basePriceCents)) this.basePriceCents = NaN;
        this.hasValidationError = false;

        this.onRadioChange = this.onRadioChange.bind(this);
        this.onAddToCartClick = this.onAddToCartClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onPageShow = this.onPageShow.bind(this);

        this.addEventListener('change', this.onRadioChange);
        if (!this.isEditMode && this.addButton) {
          this.addButton.addEventListener('click', this.onAddToCartClick);
        }
        document.addEventListener('variant:changed', this.onVariantChange);
        window.addEventListener('pageshow', this.onPageShow);

        this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.variantChange,
          (event) => this.onVariantChange(event)
        );

        requestAnimationFrame(() => {
          this.syncState();
          requestAnimationFrame(() => this.syncState());
        });
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.onPageShow);
        document.removeEventListener('variant:changed', this.onVariantChange);
        this.onVariantChangeUnsubscriber?.();
      }

      getScopeRoot() {
        if (this.isEditMode) {
          return this.closest('#CartItemEdit-content') || this;
        }

        return (
          document.getElementById(`shopify-section-${this.sectionId}`) ||
          this.closest('#CartItemEdit-content') ||
          this
        );
      }

      getEventSectionId(event) {
        return event?.data?.sectionId || event?.detail?.sectionId || null;
      }

      shouldHandleVariantChange(event) {
        const sectionId = this.getEventSectionId(event);

        if (sectionId && this.sectionId && sectionId !== this.sectionId) {
          return false;
        }

        if (this.isEditMode && !sectionId) {
          return false;
        }

        return true;
      }

      getPropertyName(radio) {
        return radio?.dataset?.propertyName || radio?.name || '';
      }

      syncState() {
        this.currentVariant = null;
        const variant = this.getCurrentVariant();
        if (variant?.id) this.dataset.variantId = String(variant.id);
        if (variant) this.dataset.available = variant.available ? 'true' : 'false';
        this.setBasePriceFromVariant(variant);
        this.refreshConditionalPanels();
        this.updateAllSummaries();
        this.updateMultiIconInfo();
        this.updateSummaryMain();
        this.updateButtonState();
        this.updatePrice();
      }

      onPageShow() {
        this.syncState();
      }

      onVariantChange(event) {
        if (!this.shouldHandleVariantChange(event)) return;

        const variant = event.detail?.variant || event.data?.variant;
        if (!variant) return;

        this.currentVariant = variant;
        if (variant.id) this.dataset.variantId = String(variant.id);
        this.dataset.available = variant.available ? 'true' : 'false';
        this.setBasePriceFromVariant(variant);
        this.refreshConditionalPanels();
        this.updateAllSummaries();
        this.updateMultiIconInfo();
        this.updateButtonState();
        requestAnimationFrame(() => {
          this.updateSummaryMain();
          this.updatePrice();
        });
      }

      getVariantPicker() {
        return this.getScopeRoot().querySelector('variant-picker');
      }

      getVariantFromPicker() {
        const picker = this.getVariantPicker();
        const raw = picker?.querySelector('[data-selected-variant]')?.textContent?.trim();
        if (!raw) return null;

        try {
          return JSON.parse(raw);
        } catch {
          return null;
        }
      }

      getCurrentVariant() {
        const fromPicker = this.getVariantFromPicker();
        if (fromPicker) {
          this.currentVariant = fromPicker;
          return fromPicker;
        }

        if (this.currentVariant?.id) return this.currentVariant;

        const variantId = parseInt(this.dataset.variantId || '', 10);
        if (!Number.isNaN(variantId) && Number.isFinite(this.basePriceCents)) {
          return {
            id: variantId,
            price: this.basePriceCents,
            available: this.dataset.available !== 'false',
          };
        }

        return null;
      }

      getVariantsSchema() {
        if (this._variantsSchema) return this._variantsSchema;

        const schemaEl = this.querySelector('[data-product-add-ons-json]');
        const raw = schemaEl?.textContent?.trim();

        if (!raw) {
          this._variantsSchema = [];
          return this._variantsSchema;
        }

        try {
          this._variantsSchema = JSON.parse(raw);
        } catch {
          this._variantsSchema = [];
        }

        return this._variantsSchema;
      }

      getVariantFromSchema(variantId) {
        if (!variantId) return null;
        return this.getVariantsSchema().find((variant) => variant.id === variantId) || null;
      }

      resolveVariantImageSrc(variant) {
        const variantId = variant?.id || this.getAddToCartVariantId();
        const schemaVariant = this.getVariantFromSchema(variantId) || variant;
        if (!schemaVariant) return null;

        return (
          schemaVariant.featured_image?.src ||
          schemaVariant.featured_media?.preview_image?.src ||
          schemaVariant.featured_media?.src ||
          null
        );
      }

      resolveVariantImageAlt(variant) {
        const variantId = variant?.id || this.getAddToCartVariantId();
        const schemaVariant = this.getVariantFromSchema(variantId) || variant;
        if (!schemaVariant) return '';

        return (
          schemaVariant.featured_image?.alt ||
          schemaVariant.featured_media?.alt ||
          schemaVariant.name ||
          ''
        );
      }

      buildSummaryImageSrc(src) {
        if (!src) return null;
        const separator = src.includes('?') ? '&' : '?';
        return src.includes('width=') ? src : `${src}${separator}width=104`;
      }

      isMainVariantSelectable() {
        return Boolean(this.getAddToCartVariantId());
      }

      getAddToCartVariantId() {
        const variantId = parseInt(this.dataset.variantId || '', 10);
        return Number.isNaN(variantId) ? null : variantId;
      }

      isVariantAvailable() {
        return this.dataset.available !== 'false';
      }

      isPanelRequired(fieldsetEl) {
        return fieldsetEl?.dataset?.required === 'true';
      }

      setBasePriceFromVariant(variant) {
        const price = Number(variant?.price);
        if (!Number.isFinite(price)) return false;
        this.basePriceCents = price;
        this.dataset.basePrice = String(price);
        return true;
      }

      resolveBasePriceCents() {
        if (Number.isFinite(this.basePriceCents)) return this.basePriceCents;

        const variant = this.getCurrentVariant();
        if (Number.isFinite(variant?.price)) {
          return this.setBasePriceFromVariant(variant) ? variant.price : NaN;
        }

        const fromData = parseInt(this.dataset.basePrice || '', 10);
        if (!Number.isNaN(fromData)) {
          this.basePriceCents = fromData;
          return fromData;
        }

        return NaN;
      }

      getAddonPriceCents() {
        let addonPrice = 0;

        this.querySelectorAll('.add-ons-options__input').forEach((panel) => {
          if (!this.isPanelVisible(panel)) return;
          const radio = panel.querySelector('input[type="radio"]:checked');
          if (!radio) return;
          const price = parseInt(radio.getAttribute('data-addon-price') || '0', 10);
          if (!Number.isNaN(price)) addonPrice += price;
        });

        return addonPrice;
      }

      getQuantity() {
        const qtyInput =
          document.getElementById(this.formId)?.querySelector('input[name="quantity"]') ||
          this.getScopeRoot().querySelector('m-quantity-input input[name="quantity"]');
        return Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);
      }

      getPriceElements() {
        const priceContainer = this.getScopeRoot().querySelector(`#Price-${this.sectionId}`);
        if (!priceContainer) return [];

        const priceEl = priceContainer.querySelector('.m-price');
        const showComparePrice = priceEl?.dataset.comparePrice === 'true';

        if (showComparePrice && priceEl?.classList.contains('m-price--on-sale')) {
          const salePrice = priceEl.querySelector('.m-price-item--sale');
          if (salePrice) return [salePrice];
        }

        const regularPrices = Array.from(
          priceEl?.querySelectorAll('.m-price__regular .m-price-item--regular') || []
        );

        return regularPrices.length
          ? regularPrices
          : Array.from(priceContainer.querySelectorAll('.m-price-item--regular')).filter((el) => !el.closest('s'));
      }

      formatPrice(cents) {
        return formatMoney(cents, window.MinimogSettings.money_format);
      }

      getSummaryElements() {
        const main = this.querySelector('.product-add-ons__summary-main');
        const mainDetails = main?.querySelector(':scope > div');

        return {
          root: this.querySelector('.product-add-ons__summary'),
          main,
          mainImage: main?.querySelector('.product-add-ons__summary-image'),
          mainTitle: mainDetails?.querySelector('span:first-child'),
          mainPrice: mainDetails?.querySelector('span:last-child'),
          addons: this.querySelector('.product-add-ons__summary-addons'),
          total: this.querySelector('.product-add-ons__summary-total .value'),
        };
      }

      updateSummaryMain() {
        const variant = this.getCurrentVariant();
        const summary = this.getSummaryElements();
        let { main, mainImage, mainPrice } = summary;
        const basePrice = this.resolveBasePriceCents();

        if (mainPrice && Number.isFinite(basePrice)) {
          mainPrice.textContent = this.formatPrice(basePrice);
        }

        const imageSrc = this.buildSummaryImageSrc(this.resolveVariantImageSrc(variant));
        if (!imageSrc) {
          if (mainImage) {
            mainImage.removeAttribute('src');
            mainImage.hidden = true;
          }
          return;
        }

        if (!mainImage && main) {
          mainImage = document.createElement('img');
          mainImage.className = 'product-add-ons__summary-image';
          mainImage.loading = 'lazy';
          const mainDetails = main.querySelector(':scope > div');
          if (mainDetails) {
            main.insertBefore(mainImage, mainDetails);
          } else {
            main.prepend(mainImage);
          }
        }

        if (!mainImage) return;

        mainImage.src = imageSrc;
        mainImage.alt = this.resolveVariantImageAlt(variant);
        mainImage.hidden = false;
      }

      updateDisplayedPrice(totalCents) {
        const formatted = this.formatPrice(totalCents);

        this.getPriceElements().forEach((el) => {
          el.textContent = formatted;
        });

        this.updateSummaryMain();

        const { total } = this.getSummaryElements();
        if (total) {
          total.textContent = formatted;
        }
      }

      formatAddonSummaryItem(radio) {
        const addonCents = parseInt(radio.getAttribute('data-addon-price') || '0', 10);
        const selectedValue = String(radio.value || '').trim();
        const labelName = String(this.getPropertyName(radio) || '').trim();
        const priceCents = Number.isNaN(addonCents) ? 0 : addonCents;
        const label = selectedValue ? `${labelName} (${selectedValue}):` : `${labelName}:`;

        return {
          label,
          value: this.formatPrice(priceCents),
        };
      }

      updateSummaryAddons() {
        const { addons } = this.getSummaryElements();
        if (!addons) return;

        addons.innerHTML = '';

        this.querySelectorAll('.add-ons-options__input').forEach((panel) => {
          if (!this.isPanelVisible(panel)) return;

          const radio = panel.querySelector('input[type="radio"]:checked');
          if (!radio) return;

          const { label, value } = this.formatAddonSummaryItem(radio);
          const row = document.createElement('div');
          row.className = 'product-add-ons__summary-item';
          row.innerHTML = `
            <span class="label">${label}</span>
            <span class="value">${value}</span>
          `;
          addons.appendChild(row);
        });

        this.updateSummaryVisibility();
      }

      hasAnySelection() {
        for (const panel of this.querySelectorAll('.add-ons-options__input')) {
          if (!this.isPanelVisible(panel)) continue;
          if (panel.querySelector('input[type="radio"]:checked')) return true;
        }

        return false;
      }

      updateSummaryVisibility() {
        const { root } = this.getSummaryElements();
        if (!root) return;

        root.classList.toggle('m:hidden', !this.hasAnySelection());
      }

      /* ---------------------------------------------------------------
       * MultiIconTextMain (product information) sync
       * -------------------------------------------------------------*/

      getInfoPanels() {
        return Array.from(this.querySelectorAll('.add-ons-options__input[data-show-info="true"]'));
      }

      getFieldsetLabel(fieldsetEl) {
        const raw = fieldsetEl.querySelector('.add-ons-options__label')?.textContent || '';
        return raw.trim().replace(/:\s*$/, '');
      }

      getFieldsetInfoValue(fieldsetEl) {
        const checked = fieldsetEl.querySelector('input[type="radio"]:checked');
        if (checked) return String(checked.value || '').trim();

        const first = fieldsetEl.querySelector('input[type="radio"]');
        return first ? String(first.value || '').trim() : '';
      }

      getMultiIconTextContainer() {
        return document.getElementById('MultiIconTextMain');
      }

      updateMultiIconInfo() {
        const container = this.getMultiIconTextContainer();
        if (!container) return;

        const seenKeys = new Set();

        this.getInfoPanels().forEach((panel) => {
          if (!this.isPanelVisible(panel)) return;

          const label = this.getFieldsetLabel(panel);
          if (!label) return;

          const value = this.getFieldsetInfoValue(panel);
          const key = panel.dataset.optionIndex || label;
          seenKeys.add(key);

          let row = container.querySelector(`[data-add-on-info="${key}"]`);

          if (!row) {
            row = document.createElement('div');
            row.setAttribute('data-add-on-info', key);
            row.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                <path fill="currentColor" d="M19.6769 10.031C18.9172 9.33839 18.0186 8.76848 17.0236 8.35903L21.2346 3.39305L17.2198 0H9.40905L5.3948 3.39131L9.60529 8.35903C8.6083 8.76848 7.71069 9.33834 6.95 10.031C5.32438 11.5093 4.31445 13.5582 4.31445 15.8163C4.31445 18.0748 5.32443 20.1229 6.95 21.6021C8.57794 23.0825 10.8307 24 13.3144 24C15.7981 24 18.0509 23.0825 19.6769 21.6021C21.3045 20.1229 22.3145 18.0748 22.3145 15.8163C22.3144 13.5582 21.3045 11.5092 19.6769 10.031ZM15.6198 7.90444L14.8214 8.84564L15.2317 7.81983L16.0507 5.78006L19.2016 3.67978L15.6198 7.90444ZM15.1367 5.91591L14.4247 7.69486L13.9257 8.94117L13.7492 9.37908H12.8232L12.6491 8.94511L12.1498 7.70053L11.4353 5.91586L15.1367 5.91591ZM11.375 5.18077L11.8484 3.47062H14.7245L15.1979 5.18077H11.375ZM17.2685 1.79864L18.761 3.05986L15.9601 4.92867L15.4924 3.2295L17.2685 1.79864ZM9.9654 1.28709H16.662L16.6711 1.29675L14.8855 2.73464H11.7432L9.95762 1.29455L9.9654 1.28709ZM9.36039 1.79864L11.0896 3.19097V3.1927L10.6104 4.92652L7.84183 3.0818L9.36039 1.79864ZM7.4995 3.76655L10.5198 5.78002L11.3402 7.83075L11.6946 8.715L11.0066 7.90444L7.4995 3.76655ZM18.6776 20.6932C17.3027 21.9418 15.4115 22.7124 13.3144 22.7124C11.2174 22.7124 9.32616 21.9418 7.95127 20.6932C6.57731 19.443 5.73079 17.7232 5.73079 15.8163C5.73079 13.9089 6.57736 12.1896 7.95127 10.9393C8.67294 10.2839 9.53683 9.76003 10.4971 9.4102L11.2501 10.2983H15.3787L16.1321 9.4102C17.092 9.76003 17.9559 10.2839 18.6776 10.9393C20.0515 12.1896 20.8985 13.9089 20.8985 15.8163C20.8985 17.7232 20.0515 19.443 18.6776 20.6932Z"></path>
              </svg>
              <span>${label}<span></span></span>
            `;
            container.appendChild(row);
          }

          const valueEl = row.querySelector('span > span');
          if (valueEl) valueEl.textContent = value;
        });

        // Remove rows for add-on options that are no longer visible (e.g. hidden conditional panels)
        container.querySelectorAll('[data-add-on-info]').forEach((row) => {
          const key = row.getAttribute('data-add-on-info');
          if (!seenKeys.has(key)) row.remove();
        });

        this.updateInfoList();
      }

      getInfoListContainer() {
        return document.getElementById('ProductInfoList');
      }

      updateInfoList() {
        const container = this.getInfoListContainer();
        if (!container) return;

        const seenKeys = new Set();

        this.getInfoPanels().forEach((panel) => {
          if (!this.isPanelVisible(panel)) return;

          const label = this.getFieldsetLabel(panel);
          if (!label) return;

          const value = this.getFieldsetInfoValue(panel);
          const key = panel.dataset.optionIndex || label;
          seenKeys.add(key);

          let row = container.querySelector(`[data-add-on-info-li="${key}"]`);

          if (!row) {
            row = document.createElement('li');
            row.setAttribute('data-add-on-info-li', key);
            row.innerHTML = `
              <span>${label}</span>
              <span></span>
            `;
            container.appendChild(row);
          }

          const valueEl = row.querySelector('span:last-child');
          if (valueEl) valueEl.textContent = value;
        });

        // Remove rows for add-on options that are no longer visible (e.g. hidden conditional panels)
        container.querySelectorAll('[data-add-on-info-li]').forEach((row) => {
          const key = row.getAttribute('data-add-on-info-li');
          if (!seenKeys.has(key)) row.remove();
        });
      }

      updateButtonState() {
        if (this.isEditMode) return;

        const labelEl = this.buttonLabel;
        const btn = this.addButton;
        if (!labelEl || !btn) return;

        const { soldOut, addToCart, unavailable } = window.MinimogStrings;

        if (!this.isVariantAvailable()) {
          labelEl.textContent = soldOut;
          btn.disabled = true;
          btn.classList.add('is-not-ready');
          return;
        }

        btn.disabled = false;

        const isReady = this.isMainVariantSelectable() && this.allRequiredSatisfied();

        if (isReady) {
          labelEl.textContent = addToCart;
          this.hasValidationError = false;
          this.setButtonError(false);
        } else {
          labelEl.textContent = this.hasValidationError ? 'Please Select Options' : 'Select Options';
        }

        btn.classList.toggle('is-not-ready', !isReady);
      }

      allRequiredSatisfied() {
        for (const panel of this.querySelectorAll('.add-ons-options__input')) {
          if (!this.isPanelVisible(panel) || !this.isPanelRequired(panel)) continue;
          if (!panel.querySelector('input[type="radio"]:checked')) return false;
        }

        return true;
      }

      updatePrice() {
        const basePrice = this.resolveBasePriceCents();
        if (Number.isNaN(basePrice)) return;

        this.updateDisplayedPrice(basePrice + this.getAddonPriceCents());
        this.updateSummaryAddons();
      }

      isPanelVisible(fieldsetEl) {
        return fieldsetEl && !fieldsetEl.hasAttribute('hidden');
      }

      refreshConditionalPanels() {
        const conditionalPanels = this.querySelectorAll('.add-ons-options__input[data-option-index]');
        const allRadios = this.querySelectorAll('input[type="radio"]');

        conditionalPanels.forEach((panel) => {
          const idx = panel.dataset.optionIndex;
          const optName = panel.dataset.optionName;
          let show = false;

          allRadios.forEach((radio) => {
            if (!radio.checked) return;
            const parentIdx = radio.dataset.conditionalIndex;
            const opts = (radio.dataset.conditionalOptions || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            if (parentIdx === idx && opts.includes(optName)) show = true;
          });

          panel.toggleAttribute('hidden', !show);
          if (!show) {
            this.setFieldsetError(panel, false);
            panel.querySelectorAll('input[type="radio"]').forEach((radio) => {
              radio.checked = false;
            });
            const valueEl = panel.querySelector('.add-ons-options__value');
            if (valueEl) valueEl.textContent = '';
          }
        });
      }

      updateSummaryForFieldset(fieldsetEl) {
        const valueEl = fieldsetEl.querySelector('.add-ons-options__value');
        if (!valueEl) return;

        const radio = fieldsetEl.querySelector('input[type="radio"]:checked');
        if (!radio) {
          valueEl.textContent = '';
          return;
        }

        let text = String(radio.value || '').trim();
        const addonCents = parseInt(radio.getAttribute('data-addon-price') || '0', 10);
        if (!Number.isNaN(addonCents) && addonCents > 0 && text) {
          let money = this.formatPrice(addonCents).trim();
          if (money.endsWith('.00')) money = money.slice(0, -3);
          if (money.endsWith(',00')) money = money.slice(0, -3);
          text = `${text} (+${money})`;
        }

        valueEl.textContent = text;
      }

      updateAllSummaries() {
        this.querySelectorAll('.add-ons-options__input').forEach((fieldsetEl) => {
          if (this.isPanelVisible(fieldsetEl)) {
            this.updateSummaryForFieldset(fieldsetEl);
          }
        });
      }

      onRadioChange(event) {
        if (event.target?.type !== 'radio') return;

        this.hasValidationError = false;
        this.setButtonError(false);
        this.refreshConditionalPanels();
        this.updateButtonState();

        const fieldsetEl = event.target.closest('.add-ons-options__input');
        if (fieldsetEl && event.target.checked) {
          this.clearFieldError(fieldsetEl);
        }

        this.querySelectorAll('.add-ons-options__input').forEach((panel) => {
          if (this.isPanelVisible(panel)) this.updateSummaryForFieldset(panel);
        });
        this.updateMultiIconInfo();
        this.updatePrice();
      }

      setFieldsetError(fieldsetEl, hasError) {
        fieldsetEl?.classList.toggle('is-error', Boolean(hasError));
      }

      setButtonError(hasError) {
        this.addButton?.classList.toggle('is-error', Boolean(hasError));
      }

      clearErrors() {
        this.querySelectorAll('.add-ons-options__input').forEach((el) => this.setFieldsetError(el, false));
      }

      clearFieldError(fieldsetEl) {
        this.setFieldsetError(fieldsetEl, false);
      }

      normalizePropertyValue(value) {
        if (typeof value !== 'string') return String(value || '').trim();

        const textarea = document.createElement('textarea');
        textarea.innerHTML = value;
        return textarea.value.trim();
      }

      preselectFieldsetFromProperties(fieldsetEl, properties) {
        fieldsetEl.querySelectorAll('input[type="radio"]').forEach((radio) => {
          const propertyName = this.getPropertyName(radio);
          const propertyValue = properties[propertyName];
          if (!propertyValue) return;

          if (this.normalizePropertyValue(propertyValue) === this.normalizePropertyValue(radio.value)) {
            radio.checked = true;
          }
        });
      }

      preselectFromProperties(properties = {}) {
        if (!properties || typeof properties !== 'object') return;

        this.querySelectorAll('.add-ons-options__input:not([data-option-index])').forEach((fieldsetEl) => {
          this.preselectFieldsetFromProperties(fieldsetEl, properties);
        });

        this.refreshConditionalPanels();

        this.querySelectorAll('.add-ons-options__input[data-option-index]').forEach((fieldsetEl) => {
          if (!this.isPanelVisible(fieldsetEl)) return;
          this.preselectFieldsetFromProperties(fieldsetEl, properties);
        });

        this.hasValidationError = false;
        this.clearErrors();
        this.updateAllSummaries();
        this.updateMultiIconInfo();
        this.updateSummaryMain();
        this.updateSummaryVisibility();
        this.updatePrice();
      }

      validate() {
        this.clearErrors();
        this.hasValidationError = false;
        this.setButtonError(false);
        let ok = true;

        this.querySelectorAll('.add-ons-options__input').forEach((fieldsetEl) => {
          if (!this.isPanelVisible(fieldsetEl) || !this.isPanelRequired(fieldsetEl)) return;
          if (!fieldsetEl.querySelector('input[type="radio"]:checked')) {
            ok = false;
            this.setFieldsetError(fieldsetEl, true);
          }
        });

        this.hasValidationError = !ok;
        this.setButtonError(!ok);
        this.updateButtonState();
        return ok;
      }

      collectMainLineProperties(bundleGroupId) {
        const groupId = String(bundleGroupId);
        const properties = {
          _has_bundle_addons: '1',
          _bundle_group_id: groupId,
        };

        this.querySelectorAll('.add-ons-options__input').forEach((fieldsetEl) => {
          if (!this.isPanelVisible(fieldsetEl)) return;
          const radio = fieldsetEl.querySelector('input[type="radio"]:checked');
          const propertyName = radio ? this.getPropertyName(radio) : '';
          if (propertyName) properties[propertyName] = String(radio.value || '');
        });

        return properties;
      }

      collectAddonLineItems(bundleGroupId, quantity = 1) {
        const items = [];
        const groupId = String(bundleGroupId);
        const addonQuantity = Math.max(1, parseInt(quantity, 10) || 1);

        this.querySelectorAll('.add-ons-options__input').forEach((fieldsetEl) => {
          if (!this.isPanelVisible(fieldsetEl)) return;

          const radio = fieldsetEl.querySelector('input[type="radio"]:checked');
          if (!radio) return;

          const variantId = radio.getAttribute('data-variant-id') || radio.dataset.variantId;
          if (!variantId) return;

          items.push({
            id: Number(variantId),
            quantity: addonQuantity,
            properties: {
              _bundle_parent_group_id: groupId,
              _bundle_option_label: this.getPropertyName(radio),
            },
          });
        });

        return items;
      }

      buildCartItems(mainVariantId, quantity) {
        const bundleGroupId = Date.now();
        return [
          {
            id: mainVariantId,
            quantity,
            properties: this.collectMainLineProperties(bundleGroupId),
          },
          ...this.collectAddonLineItems(bundleGroupId, quantity),
        ];
      }

      toggleLoading(loading) {
        const method = loading ? 'add' : 'remove';
        this.addButton?.classList[method]('loading');
      }

      showError(message) {
        MinimogTheme.Notification.show({
          target:
            this.querySelector('.add-ons__footer') ||
            this.closest('.m-cart-item-edit')?.querySelector('.m-product-form-message') ||
            document.body,
          method: 'appendChild',
          type: 'error',
          message,
          last: 3000,
          sticky: !this.querySelector('.add-ons__footer'),
        });
      }

      onAddToCartClick(event) {
        event.preventDefault();
        if (!this.isVariantAvailable()) return;
        if (!this.validate()) return;

        const mainVariantId = this.getAddToCartVariantId();
        if (!mainVariantId) {
          this.showError(window.MinimogStrings.unavailable);
          return;
        }

        const quantity = this.getQuantity();
        const items = this.buildCartItems(mainVariantId, quantity);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const data = {
          items,
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        };

        const config = fetchConfig('javascript');
        config.method = 'POST';
        config.body = JSON.stringify(data);

        const { MinimogSettings, MinimogStrings } = window;

        this.toggleLoading(true);
        this.addButton?.setAttribute('disabled', 'disabled');

        fetch(`${MinimogSettings.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || MinimogStrings.cartError);
              document.dispatchEvent(
                new CustomEvent('product-ajax:error', {
                  detail: {
                    errorMessage: response.description || response.message,
                  },
                })
              );
              return;
            }

            if (MinimogSettings.use_ajax_atc) {
              if (this.cartDrawer && MinimogSettings.enable_cart_drawer) {
                MinimogTheme.Notification.show({
                  target: this.cartDrawer.querySelector('m-cart-items'),
                  method: 'prepend',
                  type: 'success',
                  message: MinimogStrings.itemAdded,
                  delay: 400,
                });
                this.cartDrawer.open();
              } else {
                MinimogTheme.Notification.show({
                  target: this.querySelector('.add-ons__footer') || document.body,
                  method: 'appendChild',
                  type: 'success',
                  message: MinimogStrings.itemAdded,
                  last: 3000,
                  sticky: !this.querySelector('.add-ons__footer'),
                });
              }

              window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
                cart: response,
                productVariantId: String(mainVariantId),
              });

              document.dispatchEvent(
                new CustomEvent('product-ajax:added', {
                  detail: {
                    product: response,
                  },
                })
              );
            } else {
              window.location = MinimogSettings.routes.cart;
            }
          })
          .catch((error) => {
            console.error(error);
            this.showError(MinimogStrings.cartError);
          })
          .finally(() => {
            this.toggleLoading(false);
            this.updateButtonState();
          });
      }
    }
  );
}